import { format, parseISO } from 'date-fns';
import { capitalize } from 'lodash';
import { interpolate, interpolateObject } from '../../cms';
import {
  getActivity,
  getAge,
  getAllergies,
  getBreed,
  getNeutered,
} from '../../pets/functions';

export const getDogs = pets =>
  pets ? pets.map(({ id, name }) => ({ value: id, label: name })) : [];

const getLockInDate = order => {
  // If no order has been created, then we approximate the lock-in date.
  if (!order) {
    return 'up to 2 days before it ships';
  }

  const lockInDate = parseISO(order.lock_in_date);
  return lockInDate > new Date() ? format(lockInDate, 'MMM d') : null;
};

export const getNextBoxProps = ({
  subscription,
  product,
  pet,
  nextDeliveryDate,
  order,
}) => {
  const getMealPrep = {
    DY: 'dry',
    FR: 'fresh',
    MX: 'mixed',
  };
  return {
    headline: `${pet.name}'s next box`,
    arrivalDate: format(nextDeliveryDate, 'MMM d'),
    cadence: subscription.frequency,
    recipes: {
      fresh: product.variants[0].recipes.fresh,
      dry: product.variants[0].recipes.dry,
    },
    totalPacks: Number(product.variants[0].tray_count),
    totalBags: Number(product.variants[0].bag_count),
    editFreshHref: {
      pathname: '/account/edit-recipes/recipes/fresh',
      query: { pet_id: pet.id, meal_prep: getMealPrep[product.product_type] },
    },
    editBakedHref: {
      pathname: '/account/edit-recipes/recipes/baked',
      query: { pet_id: pet.id, meal_prep: getMealPrep[product.product_type] },
    },
    lockInDate: getLockInDate(order),
  };
};

export const getPlanType = product => {
  const hasDry = product.available_recipes.dry.length > 0;
  const hasFresh = product.available_recipes.fresh.length > 0;

  if (hasDry && hasFresh) {
    return 'mix';
  }
  if (hasDry) {
    return 'dry';
  }
  if (hasFresh) {
    return 'fresh';
  }
};

export const formatRecipeData = recipes => {
  const hasDry = recipes.dry?.length > 0;
  const hasFresh = recipes.fresh?.length > 0;
  let variable = '';

  if (hasDry && hasFresh) {
    variable = 'mix';

    return {
      variable: variable,
      recipes: {
        dry: capitalizeRecipeData(recipes.dry),
        fresh: capitalizeRecipeData(recipes.fresh),
      },
    };
  }
  if (hasDry) {
    variable = 'baked';

    return {
      variable: variable,
      recipes: {
        dry: capitalizeRecipeData(recipes.dry),
        fresh: [],
      },
    };
  }
  if (hasFresh) {
    variable = 'fresh';

    return {
      variable: variable,
      recipes: {
        dry: [],
        fresh: capitalizeRecipeData(recipes.fresh),
      },
    };
  }
};

export const capitalizeRecipeData = recipes => {
  return recipes.map(recipe => capitalize(recipe));
};

export const formatMealPrepText = product_type => {
  if (product_type === 'FR') return ['All fresh'];
  if (product_type === 'DY') return ['All baked'];
  return ['A mixed bowl'];
};

export const getBoxManagementNavProps = ({
  nextDeliveryDate,
  subscription,
  address,
  payment,
  pet,
  product,
}) => {
  const weeks = subscription.frequency === 1 ? 'week' : 'weeks';
  const address2Added = address.address2 ? `, ${address.address2}` : '';

  return {
    label: interpolate('Manage {{pupName}}’s box', {
      pupName: pet?.name,
    }),
    shippingDate: [`${format(nextDeliveryDate, 'PP')}`],
    frequency: [
      subscription.frequency > 1
        ? `Every ${subscription.frequency} ${weeks}`
        : `Every week`,
    ],
    shippingInfo: [
      `${address.first_name} ${address.last_name}`,
      `${address.address1}${address2Added}`,
      `${address.city}, ${address.state} ${address.zip_code}`,
    ],
    paymentInfo: payment
      ? [`Card: •••• •••• •••• ${payment.last_four_digits}`]
      : [],
    product: formatRecipeData(product.variants[0].recipes),
    mealPrep: formatMealPrepText(product.product_type),
    // ^^ link component takes an array
  };
};

export const getYourPupProps = (props, pet, customer, order) => {
  return {
    ...interpolateObject(props, {
      pupName: pet.name,
    }),
    feedingGuidelinesHref: order
      ? `/feeding-guidelines/${customer.id}/${pet.id}/${order.packing_date}/feeding_guidelines_${pet.name}.pdf`
      : null,
    buttonUrlTarget: {
      pathname: props.buttonUrlTarget,
      query: { pet_id: pet.id },
    },
    reactivateUrlTarget: {
      pathname: props.reactivateUrlTarget,
      query: { pet_id: pet.id },
    },
    pupName: pet.name,
    pupBreed: getBreed(pet.breeds),
    pupInfo: getPetInfo(pet),
    pupBreedsNames: pet.breeds.map(breed => breed.name),
  };
};

/*
  There are questions surrounding the efficacy of the current order details
  modal. It thus may change and so may this.
*/
export const getOrderDetailsProps = ({
  subscription,
  pet,
  product,
  nextDeliveryDate,
}) => {
  const subscriptionAddon = getSubscriptionAddon(subscription);
  return {
    headline: 'Order Details',
    arrivalDate: format(nextDeliveryDate, 'MMMM d'),
    petName: pet.name,
    subscription: {
      frequency: subscription.frequency,
      price: `$${subscription.price}`,
    },
    addons: subscriptionAddon ? [subscriptionAddon] : [],
    editHref: '/account/edit-delivery-date',
    /*
      TODO: the following data currently isn't surfaced. More details are
      needed to get this to work correctly.
    */
    billingDate: 'June 24, 2021',
    subtotal: '$137.00',
    discountAmount: '$68.50',
    discountPercentage: '50%',
    taxes: '$3.00',
    total: '$71.50',
    planType: getPlanType(product),
  };
};

const getSubscriptionAddon = subscription => {
  if (subscription.addons?.length > 0) {
    let quantity = 0;
    let price = 0.0;
    for (const addon of subscription.addons) {
      quantity += addon.quantity;
      price += parseFloat(addon.price);
    }
    return {
      description: 'Subscription add-ons:',
      quantity,
      price: `$${price.toFixed(2)}`,
    };
  }
};

export const getPetInfo = pet => {
  const age = getAge(pet.birth_date);
  const allergies = getAllergies(pet.allergies);
  const neutered = getNeutered(pet.gender, pet.is_neutered);
  const activity = getActivity(pet.activity_level);
  return `${age}, ${pet.weight} lbs, ${activity}, ${neutered}, and ${allergies}`;
};
